// Starts checkout for a product id and routes the result to a toast — the
// same redirect/already-owned/retry handling the paywall plate and the
// library's unlock lines all need, so it lives once rather than being
// re-implemented at each call site (premium-gate's single-product buy button,
// and both surfaces' complete-title bundle CTA).

import { useState } from "react";
import { useAction } from "convex/react";
import { toast } from "sonner";
import { api } from "@bindery/convex-retells/_generated/api";
import { errorMessage } from "@/lib/convex-error";
import { useLoggerContext } from "@/logging";
import { checkoutAttribution } from "@/lib/attribution";

/**
 * `next` is where checkout.sara.ai returns the shopper once payment
 * completes — always this device's current chapter, so a completed purchase
 * flips straight back to the reader.
 */
export function useBuyProduct(next: string): {
  buy: (productId: string) => Promise<void>;
  busy: boolean;
} {
  const createSession = useAction(api.checkout.createSession);
  const { leadId } = useLoggerContext();
  const [busy, setBusy] = useState(false);

  const buy = async (productId: string): Promise<void> => {
    setBusy(true);
    try {
      // Our own origin, so checkout returns the reader to the mirror they are
      // on rather than its peer — the two do not share a session cookie, and
      // returning to the wrong one signs them out holding a receipt. The
      // server allow-lists this before honouring it (`model/returnOrigin.ts`).
      const result = await createSession({
        productId,
        next,
        origin: window.location.origin,
        // The ad click that led here, read out of our own storage at the
        // moment of purchase — possibly weeks after the landing that
        // captured it. The server re-validates and drops what it doesn't
        // recognize, so this is a hint, never a claim.
        attribution: checkoutAttribution(leadId),
      });
      switch (result.kind) {
        case "redirect":
          window.location.assign(result.checkoutUrl);
          return; // navigating away — keep the busy state
        case "already_owned":
          toast.success("You already own this — enjoy!");
          break;
        case "retryable_error":
          toast.error("Checkout is busy", {
            description: "Please try again in a moment.",
          });
          break;
        case "failed":
          toast.error("Couldn't start checkout", {
            description: "Something went wrong on our end.",
          });
          break;
      }
    } catch (error) {
      toast.error("Couldn't start checkout", {
        description: errorMessage(error, "Please try again."),
      });
    } finally {
      setBusy(false);
    }
  };

  return { buy, busy };
}
